// Presentation helpers for the "Veille globale" source list.
//
// Naming warning: RMR and the design mock use "source" and "origine" the other
// way round. Here we keep the RMR meaning, which is also the API's:
//   - source  = the collect channel (news_radar_source.name: Concurrency, GoogleNews…)
//   - origine = the concrete feed entry (news_radar_data.from_entry)
import {
  RMR_CA,
  RMR_CA_SOFT,
  RMR_DANGER,
  RMR_GREEN,
  RMR_GREEN_BG,
  RMR_INDIGO,
  RMR_INDIGO_BG,
  RMR_ORANGE,
  RMR_RED_BG,
  RMR_RPM,
  RMR_RPM_SOFT,
  RMR_SESSIONS,
  RMR_SESSIONS_SOFT,
  RMR_SURFACE_2,
  RMR_TEXT_SECONDARY,
  RMR_TEXT_SUBTLE,
} from "@/styles/tokens";

const ICON_BASE = "/icons/sources";

// Labels mirror SOURCE_DISPLAYABLE_TEXTS in
// @/components/news-radar/common/filters/SourceFilter.vue so both stay in sync.
const SOURCE_PRESENTATION = {
  Concurrency: {
    label: "Sites concurrents",
    color: RMR_RPM,
    background: RMR_RPM_SOFT,
    icon: `${ICON_BASE}/rss.png`,
  },
  "Website (via RssApp)": {
    label: "RSS",
    color: RMR_ORANGE,
    background: RMR_CA_SOFT,
    icon: `${ICON_BASE}/rss.png`,
  },
  GoogleNews: {
    label: "Google News",
    color: RMR_INDIGO,
    background: RMR_INDIGO_BG,
    icon: `${ICON_BASE}/google_news.png`,
  },
  Gtrend: {
    label: "Google Trends",
    color: RMR_INDIGO,
    background: RMR_INDIGO_BG,
    icon: `${ICON_BASE}/google_trends.png`,
  },
  Perplexity: {
    label: "Perplexity",
    color: RMR_RPM,
    background: RMR_RPM_SOFT,
    icon: `${ICON_BASE}/perplexity.png`,
  },
  Marfeel: {
    label: "Marfeel",
    color: RMR_SESSIONS,
    background: RMR_SESSIONS_SOFT,
    icon: `${ICON_BASE}/marfeel.png`,
  },
  Reddit: {
    label: "Reddit",
    color: RMR_CA,
    background: RMR_CA_SOFT,
    icon: `${ICON_BASE}/reddit.png`,
  },
  "Rappel Conso": {
    label: "Rappel Conso",
    color: RMR_DANGER,
    background: RMR_RED_BG,
    icon: `${ICON_BASE}/rappel_conso.png`,
  },
  "X (via RssApp)": {
    label: "X",
    color: RMR_TEXT_SECONDARY,
    background: RMR_SURFACE_2,
    icon: `${ICON_BASE}/x.png`,
  },
  // The file on disk is capitalised; FeedCard.vue points at a lowercase name
  // and shows a broken icon on case-sensitive hosts.
  "Instagram (via RssApp)": {
    label: "Instagram",
    color: RMR_TEXT_SECONDARY,
    background: RMR_SURFACE_2,
    icon: `${ICON_BASE}/Instagram.png`,
  },
  "Tiktok (via RssApp)": {
    label: "TikTok",
    color: RMR_TEXT_SECONDARY,
    background: RMR_SURFACE_2,
    icon: `${ICON_BASE}/tiktok.png`,
  },
  "Linkedin (via RssApp)": {
    label: "LinkedIn",
    color: "#0A66C2",
    background: "#e8f4f9",
    icon: `${ICON_BASE}/linkedin.png`,
  },
  Youtube: {
    label: "YouTube",
    color: RMR_TEXT_SECONDARY,
    background: RMR_SURFACE_2,
    icon: `${ICON_BASE}/youtube.png`,
  },
};

const FALLBACK_PRESENTATION = {
  color: RMR_TEXT_SUBTLE,
  background: RMR_SURFACE_2,
  icon: "",
};

/**
 * Colours, label and icon for a news_radar_source name.
 * Unknown sources keep their raw name on a neutral background.
 */
export const getSourcePresentation = (sourceName) => {
  const known = SOURCE_PRESENTATION[sourceName];
  if (known) {
    return known;
  }

  return { ...FALLBACK_PRESENTATION, label: sourceName || "—" };
};

export const SCORE_LEVELS = {
  hot: { color: RMR_GREEN, background: RMR_GREEN_BG },
  mid: { color: RMR_ORANGE, background: RMR_CA_SOFT },
  low: { color: RMR_DANGER, background: RMR_RED_BG },
};

export const getScoreLevel = (score) => {
  const value = Number(score);
  if (Number.isNaN(value)) return "low";
  if (value >= 8) return "hot";
  if (value >= 5) return "mid";
  return "low";
};

/** One decimal, French comma — as specified in the design handoff. */
export const formatScore = (score) => {
  const value = Number(score);
  if (Number.isNaN(value)) return "—";
  return value.toFixed(1).replace(".", ",");
};

/** Host name of an article URL, without the www prefix. */
export const getDomain = (url) => {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (error) {
    return "";
  }
};

/**
 * `from_entry` is often a raw feed URL. Fall back to something readable when no
 * news_radar_config label matches it.
 */
export const prettifyOrigin = (fromEntry) => {
  if (!fromEntry) return "—";
  const domain = getDomain(fromEntry);
  return domain || fromEntry;
};

const RELATIVE_THRESHOLDS = [
  { limit: 60, divisor: 1, unit: "min" },
  { limit: 24 * 60, divisor: 60, unit: "h" },
];

/** "il y a 2 h" for recent items, plain date beyond 24 h. */
export const formatRelativeDate = (isoDate) => {
  if (!isoDate) return "—";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "—";

  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";

  for (const { limit, divisor, unit } of RELATIVE_THRESHOLDS) {
    if (minutes < limit) {
      return `il y a ${Math.floor(minutes / divisor)} ${unit}`;
    }
  }

  return date.toLocaleDateString("fr-FR");
};

/** "mardi 21 juillet 2026, 09:02" for the expanded detail panel. */
export const formatFullDate = (isoDate) => {
  if (!isoDate) return "—";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
